import I18n from 'i18n-js';
import React, { useState } from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import * as elog from '@reverbdotcom/commons/src/elog';
import windowWrapper from '@reverbdotcom/commons/src/window_wrapper';
import { RCButton, RCInfoBox, RCFlex } from '@reverbdotcom/cadence/components';
import { InfoIcon, ProtectionShieldIcon, TruckIcon } from '@reverbdotcom/cadence/icons/react';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';

import { Paths } from './shared/url_helpers';

const SUSPICIOUS_ACTIVITY_HC_ARTICLE_ID = '5440807127443';
const CALCULATED_SHIPPING_ARTICLE_ID = '41988368807451';
const GET_PAID_WITH_PAYPAL_HELP_CENTER_ARTICLE_ID = '41988411420699';

interface IProps {
  nagId: string
}

const TRUST_MESSAGES_NAG = 'trust_messages_nag';
const TRUST_OFFERS_NAG = 'trust_offers_nag';
const USPS_APR_2026_PRICE_NAG = 'usps_apr_2026_price_nag';
const PAYPAL_PAYOUTS_ANNOUNCEMENT_NAG = 'paypal_payouts_announcement_nag';

const USPS_APR_2026_EXTERNAL_LINK = `
https://about.usps.com/newsroom/national-releases/2026/0325-usps-announces-transportation-related-time-limited-price-change.htm
`;

const NAG_ID_TO_PARAMS = {
  [TRUST_MESSAGES_NAG]: {
    theme: 'vu-orange',
    icon: <ProtectionShieldIcon />,
    nagComponent: SuspiciousActivityNag,
  },
  [TRUST_OFFERS_NAG]: {
    theme: 'vu-orange',
    icon: <ProtectionShieldIcon />,
    nagComponent: SuspiciousActivityNag,
  },
  [USPS_APR_2026_PRICE_NAG]: {
    theme: 'blue',
    icon: <TruckIcon />,
    nagComponent: UspsApr2026PriceNag,
  },
  [PAYPAL_PAYOUTS_ANNOUNCEMENT_NAG]: {
    theme: 'blue',
    icon: <InfoIcon />,
    nagComponent: PaypalPayoutsNag,
  },
};

function PaypalPayoutsNag() {
  const helpCenterArticleUrl = useHelpCenterArticleUrl(GET_PAID_WITH_PAYPAL_HELP_CENTER_ARTICLE_ID);

  return (
    <RCFlex
      justify="space-between"
      grow
    >
      <div>
        <Translate
          tag="div"
          className="weight-bold"
          text="discovery.dismissibleDashboardBanner.paypalPayouts.title"
        />
        <Translate
          tag="div"
          text="discovery.dismissibleDashboardBanner.paypalPayouts.description"
        />
      </div>
      <div className="ml-2">
        <CoreLink
          to={helpCenterArticleUrl}
          className="button button--outlined"
          target="_blank"
        >
          {I18n.t('discovery.dismissibleDashboardBanner.paypalPayouts.learnMore')}
        </CoreLink>
      </div>
    </RCFlex>
  );
}

function SuspiciousActivityNag() {
  return (
    <div>
      <p className="mb-0">
        {I18n.t('discovery.dismissibleDashboardBanner.trustMessage')}
      </p>
      <CoreLink
        to={Paths.helpCenterArticlesUrl.expand({ articleId: SUSPICIOUS_ACTIVITY_HC_ARTICLE_ID })}
        className="color-primary td-underline"
        target="_blank"
      >
        {I18n.t('discovery.dismissibleDashboardBanner.trustLearnMore')}
      </CoreLink>
    </div>
  );
}

function UspsApr2026PriceNag() {
  return (
    <div className="ml-2">
      <Translate
        tag="span"
        text="discovery.dismissibleDashboardBanner.usps2026MessageOne"
      >
        <CoreLink
          to={USPS_APR_2026_EXTERNAL_LINK}
          target="_blank"
          className="mr-1 color-primary td-underline"
        >
          <Translate text="discovery.dismissibleDashboardBanner.usps2026MessageOneLinkText" />
        </CoreLink>
      </Translate>
      <Translate
        tag="span"
        text="discovery.dismissibleDashboardBanner.usps2026MessageTwo"
      >
        <CoreLink
          to={Paths.helpCenterArticlesUrl.expand({ articleId: CALCULATED_SHIPPING_ARTICLE_ID })}
          target="_blank"
          className="color-primary td-underline"
        >
          <Translate text="discovery.dismissibleDashboardBanner.usps2026MessageTwoLinkText" />
        </CoreLink>
      </Translate>
    </div>
  );
}

export function DismissibleDashboardBanner({ nagId }: IProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) { return null; }

  async function onDismiss() {
    try {
      await windowWrapper.jQuery.post(Paths.closeNagAlert.expand({ nagId }));
    } catch (e) {
      elog.error('discovery.dashboard_banner_dismiss.error', {}, e);
    }

    setIsDismissed(true);
  }

  const NagComponent = NAG_ID_TO_PARAMS[nagId].nagComponent;

  return (
    <div className="mt-2 mb-4">
      <RCInfoBox
        variant="subtle-outlined"
        theme={NAG_ID_TO_PARAMS[nagId].theme}
        icon={NAG_ID_TO_PARAMS[nagId].icon}
      >
        <div className="d-flex fx-justify-between fx-align-center">
          <NagComponent />
          <div className="ml-2">
            <RCButton onClick={onDismiss}>
              {I18n.t('discovery.dismissibleDashboardBanner.buttonText')}
            </RCButton>
          </div>
        </div>
      </RCInfoBox>
    </div>
  );
}

export default DismissibleDashboardBanner;
